import React from 'react'
import { PageNumber } from './Common'
import { FaChevronLeft, FaChevronRight, FaChevronDown } from 'react-icons/fa'

export default function Pagination({
    handlePageChange,
    setCurrentPage,
    currentPage,
    sidebarOpen,
    totalPages,
    length,
    setrowsPerPage,
    rowsPerPage,
    name
}) {
    if (length === 0) return null;

    const startCount = (currentPage - 1) * rowsPerPage + 1;
    const endCount = Math.min(currentPage * rowsPerPage, length);

    // Smart pagination algorithm to generate page numbers with ellipses
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always include page 1
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                // Ensure page isn't already added
                if (!pages.includes(i)) {
                    pages.push(i);
                }
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            // Always include last page
            if (!pages.includes(totalPages)) {
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between px-6 py-4 border-t border-slate-100 bg-white gap-4 flex-shrink-0 select-none">
            {/* Left side: Range Info */}
            <div className="text-sm text-slate-500 font-medium md:w-1/3 text-left">
                {`Showing `}
                <span className="text-slate-600">{startCount}</span>
                {` to `}
                <span className="text-slate-600">{endCount}</span>
                {` of `}
                <span className="text-slate-600">{length}</span>
                {` ${name.toLowerCase()}`}
            </div>

            {/* Middle side: Page Navigation */}
            <div className="flex items-center justify-center gap-1.5 md:w-1/3">
                {/* Previous Button */}
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-8 h-8 rounded-lg border border-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Previous Page"
                >
                    <FaChevronLeft size={10} />
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => {
                    if (page === '...') {
                        return (
                            <span
                                key={`ellipsis-${index}`}
                                className="w-8 h-8 flex items-center justify-center text-xs font-semibold text-slate-400"
                            >
                                ...
                            </span>
                        );
                    }

                    const isCurrent = page === currentPage;
                    return (
                        <button
                            key={`page-${page}`}
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${isCurrent
                                ? 'bg-indigo-50 text-indigo-600 border border-indigo-100/50'
                                : 'border border-slate-100 text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                                }`}
                        >
                            {page}
                        </button>
                    );
                })}

                {/* Next Button */}
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 rounded-lg border border-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Next Page"
                >
                    <FaChevronRight size={10} />
                </button>
            </div>

            {/* Right side: Rows selection */}
            <div className="flex items-center justify-end gap-2 md:w-1/3">
                <span className="text-xs text-slate-500 font-medium">Rows per page</span>
                <div className="relative">
                    <select
                        className="appearance-none bg-white border border-slate-200 hover:border-slate-300 rounded-lg pl-3 pr-8 py-1 text-xs text-slate-600 font-semibold outline-none transition-all cursor-pointer"
                        value={rowsPerPage}
                        onChange={(e) => {
                            setrowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {PageNumber.map((x) => (
                            <option key={x} value={x}>{x}</option>
                        ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none text-slate-400">
                        <FaChevronDown size={8} />
                    </div>
                </div>
            </div>
        </div>
    )
}
